import { useLocation, Link } from "react-router-dom";
import { ChevronRight, GalleryVerticalEnd } from "lucide-react";
import { Badge } from "../shadcn/ui/badge";

const HHeader = ({ rightButton }) => {
  const location = useLocation();

  // Get current page info based on route
  const getPageInfo = () => {
    const path = location.pathname;
    const pageMap = {
      "/host": {
        title: "Dashboard",
        description: "Overview of your workspace performance, bookings and key metrics",
      },
      "/host/dashboard": {
        title: "Dashboard",
        description: "Overview of your workspace performance, bookings and key metrics",
      },
      "/host/bookings": {
        title: "Bookings",
        description: "View and manage all workspace reservations and bookings",
      },
      "/host/spaces": {
        title: "Spaces & Services",
        description: "Manage your coworking spaces, meeting rooms, and events",
      },
      "/host/clients": {
        title: "Users",
        description: "Manage your client companies and their users",
      },
      "/host/invoice": {
        title: "Billing & Payments",
        description: "Track payments, invoices, and financial transactions",
        // breadcrumbs: [{ label: "Home", path: "/host" }, { label: "Billing & Payments" }], // COMMENTED OUT: Breadcrumbs removed
      },
      // NOTE: invoice list handled dynamically below based on `?tab=` so no static entry here
      "/host/invoice/add": {
        title: "Create Invoice",
        description: "Generate a new invoice for your clients",
        breadcrumbs: [
          { label: "Home", path: "/host" },
          { label: "Billing", path: "/host/invoice" },
          { label: "Create Invoice" },
        ],
      },
      "/host/analytics": {
        title: "Reports & Analytics",
        description: "Analyze booking trends, space utilization, and revenue insights",
      },
      "/host/messages": {
        title: "Communication Center",
        description: "Manage messages and communications with your clients",
      },

      "/host/settings-integrations": {
        title: "Integrations",
        description: "Connect your workspace with external tools and services to streamline your workflow",
      },
      "/host/add-space": {
        title: "Add Space",
        description: "Create a new bookable space in your building",
        breadcrumbs: [
          { label: "Home", path: "/host" },
          { label: "Spaces", path: "/host/spaces" },
          { label: "Add Space" },
        ],
      },
      "/host/settings": {
        title: "Profile",
        description: "Manage your account information, organization details, and preferences",
      },
      "/host/branding-settings": {
        title: "Branding",
        description: "Customize your workspace branding, colors, and visual identity",
      },
      "/host/settings-payment-setup": {
        title: "Payment Setup",
        description: "Configure payment methods and billing preferences",
      },
      "/host/settings-addons": {
        title: "Addons",
        description: "Manage additional services and amenities for your spaces",
      },
      "/host/settings-building-addresses": {
        title: "Building Addresses",
        description: "Manage your building locations and address information",
      },
      "/host/roles-settings": {
        title: "Roles & Permissions",
        description: "Configure user roles and access permissions",
      },
      "/host/api-settings": {
        title: "API Settings",
        description: "Manage API keys, webhooks, and integration settings",
      },
      "/host/support": {
        title: "Support Center",
        description: "Get help, find answers, and connect with our support team",
      },
      "/host/calendar": {
        title: "Calendar",
        description: "View and manage bookings in a calendar format with daily, monthly, and yearly views",
      },
      "/host/community": {
        title: "Community",
        description: "Connect with other workspace hosts and share experiences",
      },
      "/host/announcements": {
        title: "Announcements",
        description: "Create and manage announcements for your users and community",
      },
      "/host/notifications": {
        title: "Notifications",
        description: "Manage your notification preferences and settings",
      },
      "/host/settings-notifications": {
        title: "Notification Settings",
        description: "Configure how and when you receive notifications",
      },
      "/host/book-space": {
        title: "Book a Space",
        description: "Find and book the perfect workspace for your needs",
      },
      "/host/reservations": {
        title: "My Reservations",
        description: "View and manage your workspace bookings",
      },
      "/host/profile": {
        title: "Profile Settings",
        description: "Manage your account information and preferences",
      },
      "/host/my-bookings": {
        title: "My Bookings",
        description: "Manage your workspace reservations and bookings",
      },
      "/host/floor-plan": {
        title: "Floor Plan",
        description: "Explore building layouts and available spaces",
        badge: "Alpha",
      },
      "/host/user-management": {
        title: "User Management",
        description: "Manage users, invite new members, and control access permissions",
      },
      "/host/pricing": {
        title: "Membership",
        description: "View your current subscription and membership details",
        breadcrumbs: [{ label: "Home", path: "/host" }, { label: "Membership" }],
      },
      "/host/billing-management": {
        title: "Billing & Subscription",
        description: "Manage your subscription, view invoices, and upgrade your plan",
        breadcrumbs: [{ label: "Home", path: "/host" }, { label: "Billing & Subscription" }],
      },
      "/host/subscription-upgrade": {
        title: "Upgrade Subscription",
        description: "Choose the perfect plan to scale your workspace business",
        breadcrumbs: [
          { label: "Home", path: "/host" },
          { label: "Billing", path: "/host/billing-management" },
          { label: "Upgrade" },
        ],
      },
      "/host/payment-setup": {
        title: "Payment Setup",
        description: "Configure payment methods and billing preferences",
      },
      // Customer screens
      "/host/customer-dashboard": {
        title: "Dashboard",
        description: "Overview of your bookings, announcements, and workspace activities",
      },
      "/host/feed": {
        title: "Announcements & Events",
        description: "Stay updated with announcements, events, and community posts",
      },
      "/host/customer-profile": {
        title: "Profile Settings",
        description: "Manage your account information, password, and organization details",
      },
      // "/host/customer-help": {
      //   title: "Help Center",
      //   description: "Get support, find answers, and access helpful resources",
      // },
      "/host/offers-discounts": {
        title: "Offers & Discounts",
        description: "Manage discounts, offers, and packages for your services",
      },
    };

    // Handle dynamic routes
    if (path.startsWith("/host/add-space/") && path !== "/host/add-space") {
      return {
        title: "Edit Space",
        description: "Update and modify your workspace configuration",
        breadcrumbs: [
          { label: "Home", path: "/host" },
          { label: "Spaces", path: "/host/spaces" },
          { label: "Edit Space" },
        ],
      };
    }

    if (path.includes("/floor-plan-editor/")) {
      return {
        title: "Floor Plan Editor",
        description: "Design and customize your space layout with drag-and-drop tools",
      };
    }

    if (path.startsWith("/hosts/")) {
      return {
        title: "Host Profile",
        description: "View this host's details and available spaces",
      };
    }

    // Special handling for the invoices list route: show specific breadcrumbs
    if (path.startsWith("/host/invoice/list")) {
      const tab = new URLSearchParams(location.search).get("tab") || "invoices";
      if (tab === "transactions") {
        return {
          title: "Transactions",
          description: "All transactions",
          breadcrumbs: [
            { label: "Home", path: "/host" },
            { label: "Billing & Payments", path: "/host/invoice" },
            { label: "Transactions" },
          ],
        };
      }

      return {
        title: "Invoices",
        description: "All invoices",
        breadcrumbs: [
          { label: "Home", path: "/host" },
          { label: "Billing & Payments", path: "/host/invoice" },
          { label: "Invoices" },
        ],
      };
    }

    return (
      pageMap[path] || {
        title: "Host Experience",
        description: "Manage your workspace and provide great experiences for your users",
      }
    );
  };

  const pageInfo = getPageInfo();

  return (
    <>
      <header className="flex flex-shrink-0 justify-between items-center px-3 py-5 w-full h-14 bg-transparent sm:h-16 sm:px-4 md:px-6 lg:px-8">
        {/* Mobile menu trigger */}
        <button
          className="lg:hidden mr-3 p-2 rounded-md hover:bg-gray-100"
          aria-label="Open menu"
          onClick={() => {
            const event = new Event("host:openMobileSidebar");
            window.dispatchEvent(event);
          }}
        >
          <GalleryVerticalEnd className="w-6 h-6 text-gray-700" />
        </button>

        {/* Page title and description */}
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-gray-900 truncate sm:text-2xl md:text-3xl">{pageInfo.title}</h1>
            {pageInfo.badge && (
              <Badge variant="outline" className="bg-white text-gray-500 border-transparent">
                {pageInfo.badge}
              </Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">{pageInfo.description}</p>
        </div>

        {/* Right button if provided */}
        {rightButton && <div className="flex flex-shrink-0 items-center ml-3">{rightButton}</div>}
      </header>

      {/* Breadcrumbs below header */}
      {pageInfo.breadcrumbs && pageInfo.breadcrumbs.length > 0 && (
        <nav className="flex items-center px-3 py-3  sm:px-4 md:px-6 lg:px-8" aria-label="Breadcrumb">
          {pageInfo.breadcrumbs.map((crumb, index) => (
            <div key={index} className="flex items-center text-sm">
              {index > 0 && <ChevronRight className="w-4 h-4 mx-2 text-gray-400" />}
              {crumb.path ? (
                <Link to={crumb.path} className="text-gray-600 hover:text-gray-900 transition-colors">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-gray-900 font-medium">{crumb.label}</span>
              )}
            </div>
          ))}
        </nav>
      )}
    </>
  );
};

export default HHeader;
